/* eslint-disable no-unreachable */
/* eslint-disable no-case-declarations */
// src/features/users/hooks/useUserCreation.js

import { useState, useCallback, useRef } from "react";
// import moment from "moment";
import { useAuth } from "../../../contexts/AuthContext";
import { createUser } from "../../../api";
import checkIdNumberIsUsed from "../../../components/shared/functions/checkIdNumberIsUsed";
// import { parseDateString } from "../utils/validation";
import verifyIdNumber from "../../../components/shared/functions/verifyIdNumber";

/**
 * Simplified custom hook for user creation operations
 * Focuses on state management and API calls, letting backend handle validation
 */
export function useUserCreation() {
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);

  // Track submission attempts to prevent duplicate submissions
  const submissionRef = useRef(false);

  // Initialize form data with defaults
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: "",
    middleName: "",
    surname: "",
    dateOfBirth: null,
    refsGender: "M",
    refsIdentityDocType: "RSAID",
    identificationNo: "",
    nationality: "ZAF",
    operatorId: "",
    companyName: "",
    email: "",
    phone: "",
    altPhone: "",
    refsShirtSize: "",
    parentDealer: currentUser?.id || null,

    // Address Information
    province: "",
    street: "",
    suburb: "",
    postalCode: "",
    city: "",
    country: "ZAF",

    // Payment Information
    refsPaymentType: "bank",
    bank: "",
    refsBankAccountType: null,
    bankAccountNumber: "",
    branchCode: "",
    eWalletLink: "",

    // Documents
    idPhotoUrl: "",
    personPhotoUrl: "",

    // Suppliers & Products
    selectedProductTypes: [],
    selectedSuppliers: [],

    // Passport (conditional)
    passportIssueDate: null,
    passportExpiryDate: null,

    // System
    TandCs: false,
    refsTypeId: null,
  });

  /**
   * Update form data for the current step
   * @param {object} data - Form data to merge with existing state
   */
  const updateFormData = useCallback(data => {
    setFormData(prev => ({ ...prev, ...data }));
    // Clear error when data is updated
    setError(null);
  }, []);

  /**
   * Reset the form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData({
      firstName: "",
      middleName: "",
      surname: "",
      dateOfBirth: null,
      refsGender: "M",
      refsIdentityDocType: "RSAID",
      identificationNo: "",
      nationality: "ZAF",
      operatorId: "",
      companyName: "",
      email: "",
      phone: "",
      altPhone: "",
      refsShirtSize: "",
      parentDealer: currentUser?.id || null,
      province: "",
      street: "",
      suburb: "",
      postalCode: "",
      city: "",
      country: "ZAF",
      refsPaymentType: "bank",
      bank: "",
      refsBankAccountType: null,
      bankAccountNumber: "",
      branchCode: "",
      eWalletLink: "",
      idPhotoUrl: "",
      personPhotoUrl: "",
      selectedProductTypes: [],
      selectedSuppliers: [],
      passportIssueDate: null,
      passportExpiryDate: null,
      TandCs: false,
      refsTypeId: null,
    });
    setError(null);
    setCreatedUser(null);
    submissionRef.current = false;
  }, [currentUser?.id]);

  /**
   * Generate a random password for new users
   * @returns {string} A random 8-character password
   */
  const generatePassword = useCallback(() => {
    const length = 8;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";

    // Ensure at least one of each type
    password += charset[Math.floor(Math.random() * 26)]; // lowercase
    password += charset[Math.floor(Math.random() * 26) + 26]; // uppercase
    password += charset[Math.floor(Math.random() * 10) + 52]; // number

    // Fill the rest randomly
    for (let i = 3; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  }, []);

  const handleContactIdNumberValidation = async idNumber => {
    // Check if the idNumber is valid
    if (!idNumber || idNumber.length <= 3) {
      return false;
    }

    try {
      // Check if the idNumber is already used
      const isIdNumberUsed = await checkIdNumberIsUsed(idNumber);

      if (isIdNumberUsed) {
        return false;
      }

      return true;
    } catch (error2) {
      return false;
    }
  };

  // const checkIdNumberVsDOB = async (
  //   idNumber,
  //   dateOfBirth,
  //   refsIdentityDocType
  // ) => {
  //   if (refsIdentityDocType === "RSAID") {
  //     // Unix timestamp of the DOB.
  //     let idNumberDate = parseDateString(idNumber);

  //     // Add 6 hours to account for time zone differences.
  //     let birthDate = moment.unix(dateOfBirth).add(6, "hours").format("X");

  //     idNumberDate = moment.unix(idNumberDate);
  //     birthDate = moment.unix(birthDate);

  //     const isSameDay = idNumberDate.isSame(birthDate, "day");

  //     return isSameDay;
  //   }
  //   // Ignore if not RSA ID.
  //   return true;
  // };

  /**
   * Submit the form to create a new user
   * Backend handles all validation - we just send the data
   */
  const submitForm = useCallback(async () => {
    // Prevent duplicate submissions
    if (submissionRef.current) {
      return null;
    }

    setLoading(true);
    setError(null);
    submissionRef.current = true;

    try {
      // Generate password
      const password = generatePassword();

      // Prepare complete data for API call - backend handles all validation
      const apiData = {
        // Auth fields
        email: formData.email || null,
        phone: formData.phone || null,
        password,

        // Personal Information
        firstName: formData.firstName,
        middleName: formData.middleName,
        surname: formData.surname,
        dateOfBirth: formData.dateOfBirth,
        refsGender: formData.refsGender,
        refsIdentityDocType: formData.refsIdentityDocType,
        identificationNo: formData.identificationNo,
        nationality: formData.nationality,
        operatorId: formData.operatorId,
        companyName: formData.companyName,

        // Address Information
        province: formData.province,
        street: formData.street,
        suburb: formData.suburb,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,

        // Additional contact
        altPhone: formData.altPhone,

        // Payment Information
        refsPaymentType: formData.refsPaymentType,
        bank: formData.bank,
        refsBankAccountType: formData.refsBankAccountType,
        bankAccountNumber: formData.bankAccountNumber,
        branchCode: formData.branchCode,
        eWalletLink: formData.eWalletLink,

        // Documents
        idPhotoUrl: formData.idPhotoUrl,
        personPhotoUrl: formData.personPhotoUrl,

        // Passport fields (if applicable)
        passportIssueDate: formData.passportIssueDate,
        passportExpiryDate: formData.passportExpiryDate,

        // Additional fields
        refsShirtSize: formData.refsShirtSize,

        // Terms and hierarchy
        TandCs: formData.TandCs,
        parentDealer: formData.parentDealer,
        refsTypeId: formData.refsTypeId,

        // Suppliers (for later processing)
        selectedProductTypes: formData.selectedProductTypes,
        selectedSuppliers: formData.selectedSuppliers,

        // Additional context for backend
        submittedBy: currentUser?.id,
        submittedByAuthId: currentUser?.auth_id,
        lastUpdatedByAuthId: currentUser?.auth_id,
      };

      // Call the API to create user
      const response = await createUser(apiData);

      // Check if the response indicates success
      if (response?.data?.success || response?.data?.code === 201) {
        const responseData = response.data.data || response.data;

        setCreatedUser({
          id: responseData.databaseUser?.id,
          auth_id: responseData.user?.id,
          generatedPassword: password,
        });

        return responseData;
      }

      // Handle API error response
      const errorMessage = response?.data?.message || "Failed to create user";
      throw new Error(errorMessage);
    } catch (err) {
      console.error("Error creating user:", err);

      // Handle different types of errors
      let errorMessage = "Failed to create user";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Handle specific error codes
      if (err.response?.status === 409) {
        errorMessage =
          "A user with this email, phone number, or ID number already exists";
      } else if (err.response?.status === 400) {
        errorMessage =
          err.response.data?.message || "Please check all required fields";
      }

      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
      submissionRef.current = false;
    }
  }, [formData, generatePassword, currentUser]);

  /**
   * Basic client-side validation for better UX
   * Backend does the real validation
   */
  const validateStep = useCallback(
    async (stepData, stepIndex) => {
      const currentData = { ...formData, ...stepData };

      switch (stepIndex) {
        case 0: // Personal Information
          if (!currentData.firstName?.trim()) return "First name is required";
          if (!currentData.surname?.trim()) return "Surname is required";
          if (!currentData.dateOfBirth) return "Date of birth is required";

          // Validate ID Number.

          const idNumberValidationResult =
            await handleContactIdNumberValidation(currentData.identificationNo);

          if (!idNumberValidationResult) {
            return `${
              currentData.refsIdentityDocType === "RSAID" ? "ID" : "Passport"
            } Number is already in use.`;
          }

          if (currentData.refsIdentityDocType === "RSAID") {
            const verifyIdNumberResult = await verifyIdNumber(
              currentData.identificationNo,
              currentData.dateOfBirth,
              currentData.refsGender,
              currentData.nationality
            );

            if (!verifyIdNumberResult.valid) {
              return verifyIdNumberResult.message;
            }
          }

          // // eslint-disable-next-line no-case-declarations
          // const idNumberMatchesDOB = await checkIdNumberVsDOB(
          //   currentData.identificationNo,
          //   currentData.dateOfBirth,
          //   currentData.refsIdentityDocType
          // );

          // if (!idNumberMatchesDOB) {
          //   return "ID Number Does Not Match Date of Birth.";
          // }

          if (!currentData.identificationNo?.trim()) {
            return "ID/Passport number is required";
          }

          if (!currentData.phone?.trim()) return "Phone number is required";

          // Basic format checks for better UX
          if (
            currentData.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentData.email)
          ) {
            return "Please enter a valid email address";
          }

          if (currentData.phone && !/^0\d{9}$/.test(currentData.phone)) {
            return "Phone number must be 10 digits starting with 0";
          }

          if (
            currentData.altPhone?.trim() &&
            currentData.altPhone === currentData.phone
          ) {
            return "Alternative phone cannot be the same as primary phone";
          }

          // Passport validation if applicable
          if (currentData.refsIdentityDocType === "PASSPORT") {
            if (!currentData.passportIssueDate) {
              return "Passport issue date is required";
            }
            if (!currentData.passportExpiryDate) {
              return "Passport expiry date is required";
            }
          }
          break;

        case 1: // Address Information
          if (!currentData.province?.trim()) return "Province is required";
          if (!currentData.street?.trim()) return "Street address is required";
          if (!currentData.suburb?.trim()) return "Suburb is required";
          if (!currentData.city?.trim()) return "City is required";
          if (!currentData.postalCode?.trim()) return "Postal code is required";
          break;

        case 2: // Documents
          if (!currentData.idPhotoUrl) return "ID photo is required";
          if (!currentData.personPhotoUrl) return "Person photo is required";
          break;

        case 3: // Banking Information
          if (currentData.refsPaymentType === "bank") {
            if (!currentData.bank) return "Bank is required";
            if (!currentData.refsBankAccountType) {
              return "Account type is required";
            }
            if (!currentData.bankAccountNumber?.trim()) {
              return "Account number is required";
            }
            if (!currentData.branchCode?.trim()) {
              return "Branch code is required";
            }
          }
          break;

        case 4: // Suppliers & Products (optional)
          // No validation needed - this step is optional
          break;

        default:
          break;
      }

      return null; // No error
    },
    [formData]
  );

  return {
    // State
    formData,
    loading,
    error,
    createdUser,

    // Form management
    updateFormData,
    resetForm,

    // Validation (simplified)
    validateStep,

    // Submission
    submitForm,
  };
}
